/**
 * Portal message trust gate (CSAGE-314, design.md D1).
 *
 * Single predicate applied identically to all three postMessage listeners
 * in AuthBootstrap.tsx (:276-308 bootstrap, :202-217 scope, :232-245
 * refresh). Every listener calls this as its FIRST statement and returns
 * on `false`, before touching `event.data` at all:
 *
 *   isTrustedPortalMessage(event)  ⟺  event.source === window.parent
 *                                     ∧ isAllowedPortalOrigin(event.origin)
 *
 * `source` is checked first — it's a reference comparison and cannot
 * throw. It is what kills the `window.open()` opener path outright: in an
 * opened tab Sage is top-level, so `window.parent === window`, and the
 * opener's message has `event.source === <opener window>`, which is not
 * `window`. It also rejects messages posted upward from Sage's OWN child
 * iframes (a compromised kiosk report), which an origin check alone would
 * not catch, since those may be same-origin.
 *
 * Degenerate case: when Sage is not embedded, the gate reduces to
 * `event.source === window` — a same-window postMessage, which only code
 * already running on Sage's own origin can perform. No new capability is
 * granted.
 *
 * CAVEAT (design R6): this depends on Sage being embedded exactly one
 * level deep. A future wrapper iframe between the portal and Sage would
 * break `event.source === window.parent` — and would also change
 * `frame-ancestors` semantics — so such a nesting needs a deliberate
 * revisit of this gate, not a silent adjustment.
 */

import { isAllowedPortalOrigin, isDevBuild } from './portal-origins.js';

export function isTrustedPortalMessage(event: MessageEvent): boolean {
  if (event.source !== window.parent) {
    warnRejected(event.origin, 'source');
    return false;
  }
  if (!isAllowedPortalOrigin(event.origin)) {
    warnRejected(event.origin, 'origin');
    return false;
  }
  return true;
}

// Dev-only (design R4): an attacker should learn nothing from production
// behavior, but a misconfigured allow-list should be obvious immediately
// in local/dev work.
function warnRejected(origin: string, failedCheck: 'source' | 'origin'): void {
  if (!isDevBuild()) return;
  console.warn(
    `portal-message: rejected postMessage — failed ${failedCheck} check (origin: ${origin})`,
  );
}
